<template>
    <div v-show="!previewMode" class="drop-zone" :class="containerClasses">
        <drop
            class="drop-zone__area"
            @click.native="onClick"
            @drop="onDrop"
            @dragenter="onDragEnter"
            @dragleave="onDragLeave"
        />

        <div class="drop-zone__content">
            <i class="drop-zone__icon">
                <font-awesome-icon :icon="['fal', icon]" />
            </i>

            <span v-show="displayText" class="drop-zone__text">
                {{ displayText }}
            </span>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import VueScrollTo from 'vue-scrollto';

import { STATE_CLASSES } from '@/data/constants/classes';
import { SET_DRAGGING_COMPONENT, SHOW_SNACKBAR } from '@/store/types/mutation-types';

const modes = {
    CLICK: 'click',
    DROP: 'drop',
};

export default {
    props: {
        componentName: {
            type: String,
            default: '',
        },

        filled: {
            type: Boolean,
            default: false,
        },

        forceDisplay: {
            type: Boolean,
            default: false,
        },

        index: {
            type: Number,
            default: 0,
        },

        mode: {
            type: String,
            default: modes.DROP,
        },

        text: {
            type: String,
            default: 'DROP ELEMENT',
        },

        topLevel: {
            type: Boolean,
            default: false,
        },

        // once item is dropped in dropzone, should UI scroll down:
        scrollOnDrop: {
            type: Boolean,
            default: false,
        },

    },

    data() {
        return {
            isHovered: false,
        };
    },

    computed: {
        ...mapState({
            copiedElements: state => state.page.copiedElements,
            draggingComponent: state => state.page.draggingComponent,
            moving: state => state.page.moving,
            previewMode: state => state.previewMode,
        }),

        containerClasses() {
            return {
                [STATE_CLASSES.ACTIVE]: this.isActive || this.forceDisplay,
                [STATE_CLASSES.EXPANDED]: this.isExpanded || this.forceDisplay,
                [STATE_CLASSES.HOVERED]: this.isHovered,
            };
        },

        displayText() {
            const isDraggingOrPasting = this.isPasting || this.draggingComponent;
            const textFromProps = this.text.trim();

            if (!textFromProps && isDraggingOrPasting) {
                return this.componentName;
            }

            return textFromProps;
        },

        icon() {
            return this.mode === 'drop' ? 'hand-holding-water' : 'plus';
        },

        isActive() {
            return this.draggingComponent || (this.topLevel && !this.filled) || this.isPasting;
        },

        isComponentScreen() {
            return this.$route.name === 'components';
        },

        isExpanded() {
            return this.isHovered || (this.topLevel && !this.filled) || this.isPasting;
        },

        isPasting() {
            return !!this.copiedElements.elements.length;
        },
    },

    methods: {
        ...mapMutations({
            setDraggingComponent: SET_DRAGGING_COMPONENT,
            showSnackbar: SHOW_SNACKBAR,
        }),

        onClick() {
            if (this.moving) {
                this.$emit('paste', this.index);
            }
        },

        onDragEnter() {
            this.isHovered = true;
        },

        onDragLeave() {
            this.isHovered = false;
        },

        onDrop(data) {
            data.props = data.props || {};
            data.props.isNewElement = true;
            this.isHovered = false;

            this.$emit('drop', data);
            this.showCopyWarning(data);
            this.setDraggingComponent(false);

            if (this.scrollOnDrop) {
                VueScrollTo.scrollTo(this.$el, 500, {
                    easing: 'ease-in',
                    offset: -60,
                    force: true,
                });
            }
        },

        // if we are in the component editor, and the user drops a custom component or global content into another component
        // ... notify them that any changes to the original will not propagate
        showCopyWarning(data) {
            const isDataComponent = !!data.props && !!data.props.componentItemID;

            if (!this.isComponentScreen || !isDataComponent) return;

            this.showSnackbar({
                title: 'Important!',
                text: 'Any changes made to the original component will not be passed to this copy.',
            });
        },
    },
};
</script>

<style lang="scss">
.drop-zone {
    $this: &;

    $color: rgba(0, 0, 0, 0.35);
    $background-color: #fff;
    $hover-background-color: aliceblue;
    $border-color: #bfcbd9;
    $border-size: 2px;
    $height: 50px;
    $collapsed-height: 10px;
    $gutter: $height / 2;
    $transition-duration: 0.25s;

    position: relative;
    transition: all $transition-duration;
    width: 100%;
    height: 0;
    z-index: 1;
    overflow: hidden;

    &.#{ $is-active } {
        height: $collapsed-height + ($border-size * 2);
        border: dashed $border-size $border-color;
        margin: $gutter;
        width: calc(100% - #{ $gutter * 2 });
        overflow: visible;
    }

    &.#{ $is-expanded } {
        height: $height;
        padding: $gutter;
    }

    .#{ $is-locked } &,
    .#{ $is-static } & {
        display: none;
    }

    .#{ $is-static } .always-enabled & {
        display: flex;
    }

    &__area {
        @include fill(-$gutter);
        z-index: 1;

        &:hover {
            cursor: pointer;
        }
    }

    &__content {
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        color: $color;
        background-color: $background-color;
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
        font-family: $default-font-family;
        transition: all $transition-duration;
        height: $height;
        transform: scale3d(1, 0, 1) translateY(-50%);
        transition: transform 0.25s;
        transform-origin: top;

        #{ $this }.#{ $is-hovered } & {
            background-color: $hover-background-color;
        }

        #{ $this }.#{ $is-expanded } &,
        #{ $this }:hover &.#{ $is-pasting } {
            transform: scale3d(1, 1, 1) translateY(-50%);
        }
    }

    &__icon {
        font-size: 20px;
    }

    &__text {
        margin-left: 12px;
    }
}
</style>
